import React from 'react';
import DropDown from 'components/src/dropdown';
import Button from 'components/src/button';
import { getOr } from '@/utils/options';
import { TextInputForm, CheckboxForm } from '../ContactUs/FormInput';
import { Label } from 'components/src/typography';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  ContactUsTab,
  getDropdownValues,
  inputKennitalaId,
  inputNetfangId,
  inputSimiId,
  inputSkilabodId,
  inputNafnId,
  checkboxEmailId,
  checkboxSimiId,
  dropdownErindiId,
  handleSubmit,
  getReCAPTCHAId,
} from './Utils';
import { isNumber, isValidKennitalaInput } from '@/utils/utils';
import { useIsMedium } from '@/utils/hooks';

type Props = {
  selectedTab: ContactUsTab;
  showThankYouModal: React.Dispatch<React.SetStateAction<boolean>>;
};

const initialInputState = {
  [inputKennitalaId]: '',
  [inputNetfangId]: '',
  [inputSimiId]: '',
  [inputSkilabodId]: '',
  [inputNafnId]: '',
  [dropdownErindiId]: '',
};

const InputForms = ({ selectedTab, showThankYouModal }: Props) => {
  const [formInputState, setFormInputState] = React.useState<Record<string, string>>(initialInputState);
  const [checkboxState, setCheckboxState] = React.useState<Record<string, boolean>>({});
  const [errorState, setErrorState] = React.useState(false);
  const [loadingState, setLoadingState] = React.useState(false);
  const [verifiedTimestamp, setVerifiedTimestamp] = React.useState<string | null>(null);
  const [errorStateText, setErrorStateText] = React.useState<string | null>(null);
  const isMedium = useIsMedium();
  const dropdownValues = getDropdownValues(selectedTab);

  React.useEffect(() => {
    setFormInputState(initialInputState);
    setCheckboxState({});
  }, [selectedTab]);

  const isCompanyTab = selectedTab === 'fyrirtaeki';

  const dropdownStateKey = `${dropdownErindiId}-${selectedTab}`;

  const disabled = React.useMemo(() => {
    const hasEmptyFields = Object.entries(formInputState).some(
      ([key, value]) => key !== dropdownErindiId && !value.trim()
    );
    const hasEmptyDropdown = !(formInputState[dropdownStateKey] ?? '').trim();
    const hasEmptyCheckboxes = !Object.values(checkboxState).some((value) => value === true);
    return hasEmptyFields || hasEmptyDropdown || (!isCompanyTab && hasEmptyCheckboxes) || !verifiedTimestamp;
  }, [formInputState, checkboxState, verifiedTimestamp, isCompanyTab, dropdownStateKey]);

  const selectedDropdownValue = formInputState[dropdownStateKey]
    ? { value: formInputState[dropdownStateKey], label: formInputState[dropdownStateKey] }
    : null;
  const reCAPTCHAId = getReCAPTCHAId();

  const handleFormChange = (id: string, value: string) => {
    if (id === inputKennitalaId && !isValidKennitalaInput(value)) {
      return;
    }
    if (id === inputSimiId && !isNumber(value)) {
      return;
    }

    const updatedFormInputState = {
      ...formInputState,
      [id]: value,
    };

    setFormInputState(updatedFormInputState);
  };

  const handleCheckboxChange = (id: string, checked: boolean) => {
    const updatedState = { ...checkboxState };
    updatedState[id] = checked;

    for (const key in updatedState) {
      if (key !== id) {
        updatedState[key] = false;
      }
    }
    setCheckboxState(updatedState);
  };

  const resetForm = () => {
    setFormInputState(initialInputState);
    setCheckboxState({});
  };

  return (
    <>
      <DropDown
        key={dropdownStateKey}
        id={dropdownStateKey}
        placeholder={'Hvernig getum við aðstoðað þig?'}
        options={dropdownValues}
        onChange={(value) => {
          handleFormChange(dropdownStateKey, getOr(value?.label, ''));
        }}
        selectedOption={selectedDropdownValue}
        isSearchable={isMedium}
      />
      <TextInputForm
        id={inputNafnId}
        label="Nafn"
        type="text"
        value={formInputState[inputNafnId]}
        handleFormChange={handleFormChange}
      />
      <TextInputForm
        id={inputKennitalaId}
        label="Kennitala"
        type="tel"
        value={formInputState[inputKennitalaId]}
        handleFormChange={handleFormChange}
      />

      <TextInputForm
        id={inputNetfangId}
        label="Netfang"
        type="email"
        value={formInputState[inputNetfangId]}
        handleFormChange={handleFormChange}
      />

      <TextInputForm
        id={inputSimiId}
        label="Sími"
        type="tel"
        value={formInputState[inputSimiId]}
        handleFormChange={handleFormChange}
      />

      <TextInputForm
        id={inputSkilabodId}
        label="Skilaboð"
        type="textarea"
        value={formInputState[inputSkilabodId]}
        handleFormChange={handleFormChange}
      />
      {!isCompanyTab && (
        <div className="flex flex-col gap-12" key={selectedTab}>
          <CheckboxForm
            id={checkboxSimiId}
            label="Fá svar í síma"
            checked={checkboxState[checkboxSimiId]}
            disabled={selectedDropdownValue?.value === 'Vefverslun'}
            handleCheckboxChange={handleCheckboxChange}
          />

          <CheckboxForm
            id={checkboxEmailId}
            label="Fá svar með tölvupósti"
            checked={checkboxState[checkboxEmailId]}
            disabled={false}
            handleCheckboxChange={handleCheckboxChange}
          />
        </div>
      )}
      <div className="mt-8 grid gap-16">
        {reCAPTCHAId && (
          <ReCAPTCHA
            sitekey={reCAPTCHAId}
            onChange={() => setVerifiedTimestamp(JSON.stringify(new Date().getTime()))}
            onExpired={() => setVerifiedTimestamp(null)}
            onErrored={() => setVerifiedTimestamp(null)}
          />
        )}
        <Button
          label={'Senda'}
          variant={'primary'}
          onClick={() =>
            handleSubmit(
              formInputState,
              checkboxState,
              getOr(formInputState[dropdownStateKey], 'Sölu og þjónusturáðgjöf'),
              showThankYouModal,
              setErrorState,
              setErrorStateText,
              setLoadingState,
              resetForm,
              selectedTab
            )
          }
          disabled={disabled}
          loading={loadingState}
        />
        {errorState && (
          <Label classes="text-red-100">{errorStateText || 'Ekki tókst að senda skilaboð, reyndu aftur!'}</Label>
        )}
      </div>
    </>
  );
};

export default InputForms;
